import type { ReactNode } from "react";
import { RevealLines } from "@/components/Reveal";
import { TextScramble } from "@/components/TextScramble";
import { VelocitySkew } from "@/components/VelocitySkew";

export interface SectionHeaderProps {
  /** Numer porządkowy sekcji, np. "01". */
  index: string;
  eyebrow: string;
  titleLines: string[];
  lead?: ReactNode;
  tone?: "dark" | "light";
  layout?: "split" | "stacked";
  titleClassName?: string;
  className?: string;
}

/**
 * Jednolity nagłówek sekcji: [indeks] EYEBROW ——— / H2 / lead.
 */
export function SectionHeader({
  index,
  eyebrow,
  titleLines,
  lead,
  tone = "dark",
  layout = "split",
  titleClassName = "text-4xl md:text-6xl max-w-[20ch]",
  className = "",
}: SectionHeaderProps) {
  const isLight = tone === "light";
  const indexColor = isLight ? "text-black" : "text-lime-300";
  const eyebrowColor = isLight ? "text-black/70" : "text-white/60";
  const ruleColor = isLight ? "bg-black/20" : "bg-white/15";
  const titleColor = isLight ? "text-black" : "text-white";
  const leadColor = isLight ? "text-black/75" : "text-body";

  const head = (
    <>
      <div className="flex items-center gap-4 mb-5 md:mb-6">
        <span className={`font-mono text-[10px] tracking-[0.28em] ${indexColor}`}>{index}</span>
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.36em] font-medium ${eyebrowColor}`}
        >
          <TextScramble text={eyebrow} />
        </span>
        <span className={`hidden md:block h-px flex-1 ${ruleColor}`} aria-hidden />
      </div>
      <VelocitySkew maxSkewDeg={2}>
        <RevealLines as="h2" className={`h-mega ${titleClassName} ${titleColor}`} lines={titleLines} />
      </VelocitySkew>
    </>
  );

  if (layout === "stacked") {
    return (
      <div className={className}>
        {head}
        {lead ? (
          <div className={`mt-6 md:mt-8 text-base md:text-lg leading-[1.75] max-w-md ${leadColor}`}>
            {lead}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`grid md:grid-cols-[0.95fr_1.05fr] gap-6 md:gap-20 ${className}`}>
      <div>{head}</div>
      {lead ? (
        <div className={`text-base md:text-lg leading-[1.75] space-y-5 measure md:pt-10 ${leadColor}`}>
          {lead}
        </div>
      ) : (
        <div aria-hidden />
      )}
    </div>
  );
}
